// GA4 + Google Tag Manager + Meta Pixel events for the SPA. The base tags (gtag.js / gtm.js /
// fbevents.js) are injected into the HTML <head> by the Netlify edge function when an id is
// configured in admin or ENV; if the page was served without them (e.g. edge function unavailable),
// they are loaded here from /api/public/marketing. Every call is a no-op while an integration is not
// configured or while the server master switch (ANALYTICS_ENABLED) is off — then the public endpoint
// returns `enabled: false` and blank tracker ids.

let config = null;
let loading = null;

function inject(src) {
  if ([...document.scripts].some(s => s.src === src)) return;
  const el = document.createElement('script');
  el.async = true;
  el.src = src;
  document.head.appendChild(el);
}

function ensureGtag(id) {
  if (!id) return;
  if (typeof window.gtag !== 'function') {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', id, {send_page_view: false});
  }
  inject(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`);
}

function ensureGtm(id) {
  if (!id) return;
  window.dataLayer = window.dataLayer || [];
  const src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  if ([...document.scripts].some(s => s.src === src)) return;
  window.dataLayer.push({'gtm.start': Date.now(), event: 'gtm.js'});
  inject(src);
}

function ensureFbq(id) {
  if (!id || typeof window.fbq === 'function') return;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;
  s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', id);
}

export function initMarketing(fetcher = globalThis.fetch) {
  if (!loading) {
    loading = fetcher('/api/public/marketing')
      .then(r => (r.ok ? r.json() : {}))
      .catch(() => ({}))
      .then(data => {
        const on = data.enabled === true;
        config = {ga4: on ? data.ga4 || '' : '', gtm: on ? data.gtm || '' : '', meta_pixel: on ? data.meta_pixel || '' : ''};
        ensureGtm(config.gtm);
        ensureGtag(config.ga4);
        ensureFbq(config.meta_pixel);
        return config;
      });
  }
  return loading;
}

const ga = (...args) => {
  if (config?.ga4 && typeof window.gtag === 'function') window.gtag(...args);
  // GTM without GA4 in code: e-commerce events go to the dataLayer in the GA4 recommended shape,
  // the container decides which tags fire. With both configured, gtag already feeds the dataLayer.
  else if (config?.gtm && args[0] === 'event' && Array.isArray(window.dataLayer)) {
    const {items, ...rest} = args[2] || {};
    if (items) { window.dataLayer.push({ecommerce: null}); window.dataLayer.push({event: args[1], ecommerce: {...rest, items}}); }
    else window.dataLayer.push({event: args[1], ...rest});
  }
};
const fb = (...args) => { if (config?.meta_pixel && typeof window.fbq === 'function') window.fbq(...args); };

const item = (p, qty = 1) => ({
  item_id: p.sku || p.slug, item_name: p.title, item_category: p.category_slug || undefined,
  price: Number(p.price_sale || p.price || 0), quantity: qty,
});

export async function trackPageView(path, title) {
  await initMarketing();
  ga('event', 'page_view', {page_location: window.location.href, page_path: path, page_title: title});
  fb('track', 'PageView');
}

export async function trackViewItem(p) {
  await initMarketing();
  const value = Number(p.price_sale || p.price || 0);
  ga('event', 'view_item', {currency: 'UAH', value, items: [item(p)]});
  fb('track', 'ViewContent', {content_ids: [p.sku || p.slug], content_type: 'product', content_name: p.title, value, currency: 'UAH'});
}

export async function trackAddToCart(p, qty = 1) {
  await initMarketing();
  const value = Number(p.price_sale || p.price || 0) * qty;
  ga('event', 'add_to_cart', {currency: 'UAH', value, items: [item(p, qty)]});
  fb('track', 'AddToCart', {content_ids: [p.sku || p.slug], content_type: 'product', value, currency: 'UAH'});
}

export async function trackBeginCheckout(lines, total) {
  await initMarketing();
  ga('event', 'begin_checkout', {currency: 'UAH', value: Number(total || 0), items: lines.map(l => item(l, l.qty || 1))});
  fb('track', 'InitiateCheckout', {value: Number(total || 0), currency: 'UAH', num_items: lines.reduce((n, l) => n + (l.qty || 1), 0)});
}

export async function trackPurchase(order, lines) {
  if (!order) return;
  const key = `asans_purchase_sent_${order.order_number || order.id}`;
  try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, '1'); } catch {}
  await initMarketing();
  const value = Number(order.total || 0);
  ga('event', 'purchase', {transaction_id: String(order.order_number || order.id || ''), currency: 'UAH', value,
    items: (lines || []).map(l => item(l, l.qty || 1))});
  fb('track', 'Purchase', {value, currency: 'UAH', content_ids: (lines || []).map(l => l.sku || l.slug), content_type: 'product'});
}

export async function trackSearch(query) {
  if (!query) return;
  await initMarketing();
  ga('event', 'search', {search_term: query});
  fb('track', 'Search', {search_string: query});
}

export const _test = {reset: () => { config = null; loading = null; }};
